import type { Locale } from '@marine/i18n';
import type { ApiClient, Schemas } from '../generated/laravel-typed-openapi';

/**
 * The signed-in user's settings over REST — `PATCH /users/settings`.
 *
 * Replaces the Lumen `updateUserSettings` mutation, and the difference worth knowing is that this
 * endpoint is a true patch: every field on it is optional and the ones left out are left alone. The
 * mutation had no such shape — it took the whole settings object every time, so changing one row
 * meant sending the other five back exactly as they were read, and a stale read overwrote a change
 * made on another device. Callers here send only what the user actually changed.
 *
 * The 200 body is not read. It carries the settings as the backend now holds them, but the app
 * still reads settings through the Lumen `userSettings` query, and one of the two has to be the
 * source of truth — so a write refreshes that query rather than seeding a second copy from its own
 * echo.
 *
 * The two currencies live here too. They used to hang off the profile, and `PATCH /users/profile`
 * no longer accepts them — everything the preferences screens write is one endpoint now.
 */

/**
 * A settings change, with the values as the app holds them: text, exactly as the settings table
 * stores them and as `getSettingByKey` hands them back.
 *
 * Deliberately looser than the generated request type, which spells each field out as the union of
 * values the backend accepts (`'imperial' | 'metric'`, the eight date formats, and so on). Those
 * unions are right, and this is not an attempt to get around them: the app carries every setting as
 * a string read out of the settings table, and the option lists the pickers are built from are the
 * same strings. Widening here keeps the one conversion in one place — see `updateUserSettings` —
 * instead of casting at every call site.
 */
export type UserSettingsPatch = Partial<{
  unit: string;
  date_format: string;
  time_format: string;
  first_day_of_week: string;
  decimal_symbol: string;
  digit_grouping_symbol: string;
  timezone_id: number;
  language_id: number;
  /** The language as an ISO code (`en`, `it`). Preferred over `language_id`, whose ids differ between environments. */
  language: Locale | null;
  currency_id: number;
  base_currency_id: number;
}>;

/** The body the endpoint declares: the generated request plus the two symbol fields. */
type UpdateSettingsBody = Schemas.UpdateSettingsRequest &
  Partial<{ decimal_symbol: '.' | ','; digit_grouping_symbol: '.' | ',' }>;

/**
 * Everything the account has set, from `GET /users/settings`.
 *
 * One resource for what used to take two reads: the units, formats and symbols that live in the
 * settings table, and the time zone and currencies that used to be read off the user.
 */
export const getUserSettings = async (apiClient: ApiClient): Promise<Schemas.SettingsResource> =>
  await apiClient.get('/users/settings');

/**
 * Applies one or more settings, leaving every setting not named in `patch` as it is.
 *
 * Throws `TypedStatusError` on any 4xx/5xx — the generated client's default — so a rejected value
 * reaches the caller as a failed mutation rather than as a silent no-op. Pair a 422 with
 * `readValidationErrors` to find out which setting the API refused.
 */
export const updateUserSettings = async (apiClient: ApiClient, patch: UserSettingsPatch): Promise<void> => {
  // The one place the app's strings meet the endpoint's unions. A value the endpoint does not
  // accept is refused with a 422 naming the field, which is what the caller reports.
  await apiClient.patch('/users/settings', { body: patch as UpdateSettingsBody });
};
